import type { ITourTranslation } from '@/shared/api/types/tour-types'
import { useTranslation } from 'react-i18next'
import { TourTranslationSection } from '@/features/tour/tour-translation-section'
import { TranslationsSkeleton } from '@/shared/ui/translations-skeleton'
import { FormSectionCard } from '@/shared/ui/form-section-card'
import { SectionSaveButton } from '@/shared/ui/section-save-button'

interface TranslationsCardProps {
  isEdit: boolean
  isFormReady: boolean
  languages: string[]
  translations: ITourTranslation[]
  onChange: (value: ITourTranslation[]) => void
  /** Id the translation jobs are filed under (tour id, or the per-tenant draft id when creating). */
  entityId: string
  /** Number of translation jobs that finished successfully but are not applied yet. */
  runningJobCount: number
  isSaving: boolean
  isUpdating: boolean
  onSave: () => void
}

/** Name/description of the tour in every tenant language, with AI translation. */
export function TranslationsCard({
  isEdit,
  isFormReady,
  languages,
  translations,
  onChange,
  entityId,
  runningJobCount,
  isSaving,
  isUpdating,
  onSave,
}: TranslationsCardProps) {
  const { t } = useTranslation()
  const entityLabel = translations.find((item) => item.name.trim())?.name ?? t('tour.title')
  const entityPath = typeof window === 'undefined' ? undefined : window.location.pathname

  return (
    <FormSectionCard
      title={t('tour.translations')}
      action={
        isEdit && <SectionSaveButton disabled={isUpdating} saving={isSaving} onSave={onSave} />
      }
    >
      {isFormReady ? (
        <>
          {runningJobCount > 0 && (
            <p className="mb-3 text-sm text-blue-700">
              {t('jobs.runningCount', { count: runningJobCount })}
            </p>
          )}
          <TourTranslationSection
            languages={languages}
            translations={translations}
            onChange={onChange}
            entityId={entityId}
            entityLabel={entityLabel}
            {...(entityPath ? { entityPath } : {})}
          />
        </>
      ) : (
        <TranslationsSkeleton />
      )}
    </FormSectionCard>
  )
}
